import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Checkbox,
} from '@mui/material';
import { JiraIssue } from '../types';

interface JiraIssuesListProps {
  issues: JiraIssue[];
  jiraBaseUrl?: string;
  showCheckboxes?: boolean;
  onSelectionChange?: (selectedKeys: string[]) => void;
  initialSelectedKeys?: string[];
}

const JiraIssuesList: React.FC<JiraIssuesListProps> = ({
  issues,
  jiraBaseUrl,
  showCheckboxes = false,
  onSelectionChange,
  initialSelectedKeys = [],
}) => {
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const prevInitialKeysStringRef = useRef<string>('');
  const prevIssuesKeysRef = useRef<string>('');
  const isInitializedRef = useRef(false);
  const isInternalUpdateRef = useRef(false);

  useEffect(() => {
    const chosenKeys = issues.filter((issue) => issue.chosen).map((issue) => issue.key);
    const initialKeysString = [...initialSelectedKeys].sort().join(',');
    const issuesKeysString = chosenKeys.sort().join(',');

    // При первой инициализации объединяем initialSelectedKeys и chosen
    if (!isInitializedRef.current) {
      const allSelectedKeys = new Set([...initialSelectedKeys, ...chosenKeys]);
      setSelectedKeys(allSelectedKeys);
      isInitializedRef.current = true;
      prevInitialKeysStringRef.current = initialKeysString;
      prevIssuesKeysRef.current = issuesKeysString;
      return;
    }

    // После инициализации обновляем только при изменении issues (для новых chosen)
    // или при изменении initialSelectedKeys (используем их напрямую)
    const initialKeysChanged = prevInitialKeysStringRef.current !== initialKeysString;
    const issuesKeysChanged = prevIssuesKeysRef.current !== issuesKeysString;

    if (initialKeysChanged) {
      // При изменении initialSelectedKeys используем их напрямую
      // Они уже содержат актуальный выбор пользователя
      // Не добавляем chosen автоматически, чтобы не перезаписывать выбор пользователя
      setSelectedKeys(new Set(initialSelectedKeys));
      isInternalUpdateRef.current = true;
      prevInitialKeysStringRef.current = initialKeysString;
    }

    if (issuesKeysChanged && !initialKeysChanged) {
      // При изменении issues (но не initialSelectedKeys) добавляем новые chosen
      setSelectedKeys((prevSelectedKeys) => {
        const newSelected = new Set(prevSelectedKeys);
        chosenKeys.forEach((key) => newSelected.add(key));
        isInternalUpdateRef.current = true;
        return newSelected;
      });
      prevIssuesKeysRef.current = issuesKeysString;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issues, initialSelectedKeys.join(',')]);

  useEffect(() => {
    // Вызываем onSelectionChange только если это не внутреннее обновление из initialSelectedKeys
    if (onSelectionChange && !isInternalUpdateRef.current) {
      onSelectionChange(Array.from(selectedKeys));
    }
    isInternalUpdateRef.current = false;
  }, [selectedKeys, onSelectionChange]);

  const handleToggleSelect = (key: string) => {
    const newSelected = new Set(selectedKeys);
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedKeys(newSelected);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedKeys(new Set(issues.map((issue) => issue.key)));
    } else {
      setSelectedKeys(new Set());
    }
  };

  const isAllSelected = issues.length > 0 && selectedKeys.size === issues.length;
  const isIndeterminate = selectedKeys.size > 0 && selectedKeys.size < issues.length;

  if (issues.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1" color="text.secondary">
          Нет задач для отображения
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer
      component={Paper}
      elevation={3}
      sx={{
        height: '100%',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        '&::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'background.default',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'divider',
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: 'text.secondary',
          },
        },
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {showCheckboxes && (
              <TableCell padding="checkbox" sx={{ fontWeight: 'bold' }}>
                <Checkbox
                  indeterminate={isIndeterminate}
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                />
              </TableCell>
            )}
            <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Ключ</TableCell>
            <TableCell sx={{ fontWeight: 'bold', minWidth: 250 }}>Название</TableCell>
            <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Тип</TableCell>
            <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Статус</TableCell>
            <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Автор</TableCell>
            <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Исполнитель</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {issues.map((issue) => (
            <TableRow
              key={issue.id}
              hover
              sx={{
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              {showCheckboxes && (
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedKeys.has(issue.key)}
                    onChange={() => handleToggleSelect(issue.key)}
                  />
                </TableCell>
              )}
              <TableCell>
                {jiraBaseUrl ? (
                  <Link
                    href={`${jiraBaseUrl}/browse/${issue.key}`}
                    target="_blank"
                    rel="noreferrer"
                    underline="hover"
                    sx={{ fontWeight: 500 }}
                  >
                    {issue.key}
                  </Link>
                ) : (
                  <Typography variant="body2" fontWeight={500}>
                    {issue.key}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {issue.summary}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip label={issue.issuetype} size="small" variant="outlined" />
              </TableCell>
              <TableCell>
                <Chip label={issue.status} size="small" color="primary" />
              </TableCell>
              <TableCell>
                <Typography variant="body2">{issue.reporter || '—'}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{issue.assignee || '—'}</Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default JiraIssuesList;
